import { useCallback, useRef, useState } from "react";
import { Paperclip, X } from "@phosphor-icons/react";
import {
  UploadViewWrapper,
  DropZone,
  DropZoneTitle,
  DropZoneText,
  UploadFileList,
  UploadFileItem,
  UploadRemoveBtn,
  UploadProgress,
  UploadProgressBar,
  PrimaryBtn,
  SecondaryBtn,
  BtnRow,
  MutedText,
} from "../MailIntakeStyled";

const formatSize = (bytes) => {
  if (bytes < 1048576) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const UploadView = () => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const inputRef = useRef(null);

  const handleFiles = useCallback((fileList) => {
    const pdfs = Array.from(fileList).filter(
      (f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")
    );
    setFiles((prev) => [...prev, ...pdfs]);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragActive(false);
      if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
    },
    [handleFiles]
  );

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => setDragActive(false);

  const handleBrowseClick = () => inputRef.current?.click();

  const handleInputChange = (e) => {
    if (e.target.files?.length) handleFiles(e.target.files);
    e.target.value = "";
  };

  const handleRemove = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    setUploading(true);
    setUploadProgress(0);

    // Simulated upload progress for mock mode
    const total = files.length;
    for (let i = 0; i < total; i++) {
      await new Promise((r) => setTimeout(r, 300 + Math.random() * 400));
      setUploadProgress(Math.round(((i + 1) / total) * 100));
    }

    setUploading(false);
    setFiles([]);
    setUploadProgress(0);
  };

  return (
    <UploadViewWrapper>
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        multiple
        style={{ display: "none" }}
        onChange={handleInputChange}
      />

      <DropZone
        active={dragActive}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleBrowseClick}
      >
        <Paperclip
          size={40}
          weight="regular"
          style={{ marginBottom: 12, opacity: 0.6 }}
          aria-hidden
        />
        <DropZoneTitle>
          {dragActive ? "Drop files here" : "Drag & drop PDF files"}
        </DropZoneTitle>
        <DropZoneText>or click to browse — PDF files only</DropZoneText>
      </DropZone>

      {files.length > 0 && (
        <UploadFileList>
          {files.map((file, idx) => (
            <UploadFileItem key={`${file.name}-${idx}`}>
              <div>
                <span style={{ fontWeight: 500 }}>{file.name}</span>
                <MutedText style={{ marginLeft: 8 }}>
                  {formatSize(file.size)}
                </MutedText>
              </div>
              {!uploading && (
                <UploadRemoveBtn
                  aria-label={`Remove ${file.name}`}
                  onClick={() => handleRemove(idx)}
                >
                  <X size={14} weight="bold" />
                </UploadRemoveBtn>
              )}
            </UploadFileItem>
          ))}

          {uploading && (
            <UploadProgress>
              <UploadProgressBar percent={uploadProgress} />
            </UploadProgress>
          )}

          <BtnRow style={{ marginTop: 8 }}>
            <PrimaryBtn onClick={handleUpload} disabled={uploading}>
              {uploading
                ? `Uploading... ${uploadProgress}%`
                : `Upload ${files.length} file${files.length !== 1 ? "s" : ""}`}
            </PrimaryBtn>
            {!uploading && (
              <SecondaryBtn onClick={() => setFiles([])}>Clear</SecondaryBtn>
            )}
          </BtnRow>
        </UploadFileList>
      )}
    </UploadViewWrapper>
  );
};

export default UploadView;
